
import { AppState, HabitType } from '@/types';
import { calculateHabiticaReward, getHabiticaColorState, shouldResetDailyCompletions, HABITICA_VALUE_CONFIG, calculateDailyMissPenalty, calculateNegativeHabitPenalty, calculateDailyDamage, getConstitutionModifier } from '@/lib/habitValueSystem';
import { Target } from 'lucide-react';
import { generateActivityDrops } from '@/lib/petCollectionSystem';

export interface HabiticaAction {
  type: string;
  payload?: any;
}

function calculateLevel(experience: number): number {
  return Math.floor(experience / 100) + 1;
}

export function habiticaReducer(state: AppState, action: HabiticaAction): AppState {
  switch (action.type) {
    case 'CREATE_HABIT': {
      console.log('🎯 CREATE_HABIT reducer called!', action.payload);
      const { habit } = action.payload;
      console.log('📝 Creating habit:', habit.name, 'Type:', habit.type);
      console.log('📊 Current habits count:', state.habits.length);
      
      const newHabit: HabitType = {
        ...habit,
        habitValue: HABITICA_VALUE_CONFIG.DEFAULT_VALUE,
        dailyCompletions: 0,
        colorState: 'yellow'
      };
      
      const newState = {
        ...state,
        habits: [...state.habits, newHabit]
      };
      
      console.log('✅ CREATE_HABIT reducer completed. New habits count:', newState.habits.length);
      return newState;
    }

    case 'UPDATE_HABIT': {
      console.log('🎯 UPDATE_HABIT reducer ENTRY POINT - Action received:', action.payload);
      console.log('🔍 UPDATE_HABIT reducer:', action.payload);
      const { habitId, updatedHabit } = action.payload;
      const habitIndex = state.habits.findIndex(h => h.id === habitId);
      if (habitIndex === -1) {
        console.log('❌ UPDATE_HABIT - Habit not found in state:', habitId);
        return state;
      }

      console.log('✅ UPDATE_HABIT - Updating habit at index:', habitIndex, 'with data:', updatedHabit);
      console.log('🔍 UPDATE_HABIT - Previous habit:', state.habits[habitIndex]);
      console.log('🔍 UPDATE_HABIT - New habit frequency data:', {
        frequency: updatedHabit.frequency,
        repeatType: updatedHabit.repeatType,
        repeatEvery: updatedHabit.repeatEvery,
        repeatOn: updatedHabit.repeatOn
      });
      const updatedHabits = [...state.habits];
      updatedHabits[habitIndex] = updatedHabit;

      return {
        ...state,
        habits: updatedHabits
      };
    }

    case 'COMPLETE_HABIT': {
      const { habitId } = action.payload;
      console.log('🎯 COMPLETE_HABIT - Starting unified reward system:', { habitId });
      
      const habitIndex = state.habits.findIndex(h => h.id === habitId);
      if (habitIndex === -1) {
        console.log('❌ COMPLETE_HABIT - Habit not found:', habitId);
        return state;
      }

      const habit = state.habits[habitIndex];
      console.log('🎯 COMPLETE_HABIT - Found habit:', { 
        name: habit.name, 
        type: habit.type, 
        basePoints: habit.basePoints,
        originalActivityId: habit.originalActivityId,
        taskValue: habit.habitValue,
        completedToday: habit.completedToday 
      });
      
      // For dailies, only allow one completion per day
      if (habit.type === 'daily' && habit.completedToday) {
        console.log('❌ COMPLETE_HABIT - Daily already completed today');
        return state;
      }

      // Reset daily completions if it's a new day
      const shouldReset = shouldResetDailyCompletions(habit);
      const currentDailyCompletions = shouldReset ? 0 : habit.dailyCompletions;

      // Calculate Habitica XP and Gold rewards (but use original points for points)
      const rewardCalculation = calculateHabiticaReward({
        ...habit,
        dailyCompletions: currentDailyCompletions
      });

      // Use original activity points if available, otherwise fall back to habit points
      const pointsToAward = habit.basePoints || habit.points || rewardCalculation.experience;
      
      // Generate pet drops for habit completions (accountability reward)
      const petDrops = generateActivityDrops(pointsToAward);

      console.log('🎯 UNIFIED REWARD CALCULATION:', {
        basePoints: habit.basePoints,
        pointsAwarded: pointsToAward,
        habiticaXP: rewardCalculation.experience,
        habiticaGold: rewardCalculation.gold,
        petDrops: petDrops.length,
        taskValueChange: rewardCalculation.newTaskValue - habit.habitValue
      });

      const updatedHabit: HabitType = {
        ...habit,
        completedToday: habit.type === 'daily' ? true : habit.completedToday,
        lastCompleted: new Date(),
        lastCompletionDate: new Date(),
        streak: habit.streak + 1,
        habitValue: rewardCalculation.newTaskValue,
        dailyCompletions: currentDailyCompletions + 1,
        colorState: getHabiticaColorState({
          ...habit,
          habitValue: rewardCalculation.newTaskValue,
          dailyCompletions: currentDailyCompletions + 1,
          lastCompletionDate: new Date()
        })
      };

      const updatedHabits = [...state.habits];
      updatedHabits[habitIndex] = updatedHabit;

      // Create activity record with unified points
      const habitActivity = {
        id: crypto.randomUUID(),
        name: habit.name,
        activityType: { 
          id: habit.originalActivityId || habit.name, 
          name: habit.name, 
          points: pointsToAward, // Use unified points
          icon: Target, 
          color: '#8B5CF6', 
          description: habit.description || habit.name 
        },
        points: pointsToAward, // Unified points
        timestamp: new Date(),
        description: `${habit.type === 'daily' ? 'Daily' : habit.type === 'habit' ? 'Habit' : 'Todo'}: ${habit.description || habit.name}`,
        userId: state.user?.id || '',
        userName: state.user?.name || '',
        likes: [],
        likedBy: [],
        comments: [],
        duration: null,
        notes: null,
        photoUrl: null
      };

      const newExperience = state.experience + rewardCalculation.experience;
      const newLevel = calculateLevel(newExperience);

      const newState = {
        ...state,
        habits: updatedHabits,
        activities: [...state.activities, habitActivity],
        totalPoints: state.totalPoints + pointsToAward, // Unified points
        currentWeekPoints: state.currentWeekPoints + pointsToAward, // Unified points
        experience: newExperience, // Habitica XP
        gold: state.gold + rewardCalculation.gold, // Habitica Gold
        health: Math.min(100, state.health + (pointsToAward >= 4 ? 5 : 2)), // Health bonus for habit completion
        consecutiveDaysCompleted: state.consecutiveDaysCompleted + 1,
        // Add pet drops to collection for habit completions (accountability reward)
        petCollection: state.petCollection ? {
          ...state.petCollection,
          inventory: [...state.petCollection.inventory, ...petDrops.map(drop => ({
            ...drop,
            user_id: state.user!.id
          }))]
        } : state.petCollection
      };

      console.log('✅ UNIFIED HABIT COMPLETION:', { 
        pointsAwarded: pointsToAward,
        xpGained: rewardCalculation.experience,
        goldGained: rewardCalculation.gold,
        petDrops: petDrops.length,
        newTaskValue: rewardCalculation.newTaskValue
      });

      return newState;
    }

    case 'CLICK_NEGATIVE_HABIT': {
      const { habitId, constitution } = action.payload;
      const habitIndex = state.habits.findIndex(h => h.id === habitId);
      if (habitIndex === -1) return state;

      const habit = state.habits[habitIndex];
      if (habit.type !== 'habit') return state;

      console.log('❌ CLICK_NEGATIVE_HABIT - Applying precise HP loss:', { habitId, constitution });

      const { newTaskValue, hpLoss } = calculateNegativeHabitPenalty(habit, constitution);

      const updatedHabit: HabitType = {
        ...habit,
        habitValue: newTaskValue,
        colorState: getHabiticaColorState({ ...habit, habitValue: newTaskValue })
      };

      const updatedHabits = [...state.habits];
      updatedHabits[habitIndex] = updatedHabit;

      const newHealth = Math.max(0, state.health - hpLoss);

      return {
        ...state,
        habits: updatedHabits,
        health: newHealth,
        damageHistory: [
          ...state.damageHistory,
          { date: new Date(), amount: hpLoss, reason: `Negative habit: ${habit.name}` }
        ].slice(-10)
      };
    }

    case 'UNCOMPLETE_HABIT': {
      const { habitId } = action.payload;
      console.log('🔄 UNCOMPLETE_HABIT - Starting uncomplete for habit:', habitId);
      const habitIndex = state.habits.findIndex(h => h.id === habitId);
      if (habitIndex === -1) {
        console.log('❌ UNCOMPLETE_HABIT - Habit not found:', habitId);
        return state;
      }

      const habit = state.habits[habitIndex];
      console.log('🔄 UNCOMPLETE_HABIT - Found habit:', { 
        name: habit.name, 
        type: habit.type, 
        completedToday: habit.completedToday,
        dailyCompletions: habit.dailyCompletions 
      });
      
      // Allow uncompleting dailies if they were completed today
      if (habit.type === 'daily' && !habit.completedToday) {
        console.log('❌ UNCOMPLETE_HABIT - Daily was not completed today');
        return state;
      }
      
      // Only allow uncompleting if the habit was actually completed
      if (habit.type === 'habit' && habit.dailyCompletions === 0) return state;

      // Calculate the rewards that need to be reversed
      const rewardCalculation = calculateHabiticaReward({
        ...habit,
        dailyCompletions: Math.max(0, habit.dailyCompletions - 1)
      });

      const updatedHabit: HabitType = {
        ...habit,
        completedToday: (habit.type === 'todo' || habit.type === 'daily') ? false : habit.completedToday,
        lastCompleted: (habit.type === 'todo' || habit.type === 'daily') ? undefined : habit.lastCompleted,
        streak: Math.max(0, habit.streak - 1),
        dailyCompletions: habit.type === 'habit' 
          ? Math.max(0, habit.dailyCompletions - 1)
          : habit.dailyCompletions,
        colorState: getHabiticaColorState({
          ...habit,
          dailyCompletions: habit.type === 'habit' ? Math.max(0, habit.dailyCompletions - 1) : habit.dailyCompletions
        })
      };

      const updatedHabits = [...state.habits];
      updatedHabits[habitIndex] = updatedHabit;

      const newExperience = Math.max(0, state.experience - rewardCalculation.experience);
      const newLevel = calculateLevel(newExperience);

      return {
        ...state,
        habits: updatedHabits,
        experience: newExperience,
        
        gold: Math.max(0, state.gold - rewardCalculation.gold),
        consecutiveDaysCompleted: Math.max(0, state.consecutiveDaysCompleted - 1)
      };
    }

    case 'APPLY_DAILY_DAMAGE': {
      const { incompleteDailies, constitution, newDamageCheck } = action.payload;
      
      // Calculate precise HP loss for each missed daily
      let totalDamage = 0;
      const updatedHabits = state.habits.map(habit => {
        if (habit.type === 'daily' && !habit.completedToday) {
          const { newTaskValue, hpLoss } = calculateDailyMissPenalty(habit, constitution);
          totalDamage += hpLoss;
          
          return {
            ...habit,
            habitValue: newTaskValue,
            colorState: getHabiticaColorState({ ...habit, habitValue: newTaskValue })
          };
        }
        return habit;
      });
      
      const newHealth = Math.max(0, state.health - totalDamage);
      
      console.log('💔 APPLY_DAILY_DAMAGE - Precise damage calculation:', {
        missedDailies: incompleteDailies.length,
        constitution,
        totalDamage,
        newHealth
      });
      
      return {
        ...state,
        habits: updatedHabits,
        health: newHealth,
        lastDamageCheck: newDamageCheck,
        perfectDayStreak: 0,
        damageHistory: [
          ...state.damageHistory,
          { 
            date: newDamageCheck, 
            amount: totalDamage, 
            reason: `Missed ${incompleteDailies.length} daily habit${incompleteDailies.length > 1 ? 's' : ''} (Precise Habitica damage)` 
          }
        ].slice(-10)
      };
    }

    case 'TAKE_DAMAGE': {
      const { amount, reason, timestamp } = action.payload;
      const newHealth = Math.max(0, state.health - amount);
      
      return {
        ...state,
        health: newHealth,
        damageHistory: [
          ...state.damageHistory,
          { date: timestamp, amount, reason }
        ].slice(-10)
      };
    }



    case 'DELETE_HABIT': {
      const { habitId } = action.payload;
      console.log('🗑️ DELETE_HABIT reducer called:', habitId);
      console.log('🗑️ Before delete - habits count:', state.habits.length);
      const filteredHabits = state.habits.filter(habit => habit.id !== habitId);
      console.log('🗑️ After delete - habits count:', filteredHabits.length);
      
      return {
        ...state,
        habits: filteredHabits
      };
    }

    case 'RESET_DAILIES': 
    case 'RESET_DAILY_COUNTERS': {
      console.log('🌅 Daily reset reducer - resetting daily counters');
      
      // For RESET_DAILY_COUNTERS, use the provided habits, otherwise calculate them
      let updatedHabits;
      if (action.type === 'RESET_DAILY_COUNTERS' && action.payload?.habits) {
        updatedHabits = action.payload.habits;
      } else {
        // Reset all daily habits and update task values
        updatedHabits = state.habits.map(habit => {
          if (habit.type === 'daily') {
            return { 
              ...habit, 
              completedToday: false,
              dailyCompletions: 0,
              colorState: getHabiticaColorState({ ...habit, dailyCompletions: 0 })
            };
          } else if (habit.type === 'habit') {
            // Update color states for regular habits
            const shouldReset = shouldResetDailyCompletions(habit);
            return {
              ...habit,
              dailyCompletions: shouldReset ? 0 : habit.dailyCompletions,
              colorState: getHabiticaColorState({
                ...habit,
                dailyCompletions: shouldReset ? 0 : habit.dailyCompletions
              })
            };
          }
          return habit;
        });
      }

      // Calculate actual activities logged today from the activities array
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const actualActivitiesLoggedToday = state.activities?.filter(activity => {
        const activityDate = new Date(activity.timestamp);
        activityDate.setHours(0, 0, 0, 0);
        return activityDate.getTime() === today.getTime();
      }).length || 0;

      return {
        ...state,
        habits: updatedHabits,
        activitiesLoggedToday: actualActivitiesLoggedToday, // Use calculated value, don't reset to 0
        lastDailyReset: action.type === 'RESET_DAILY_COUNTERS' && action.payload?.lastDailyReset 
          ? action.payload.lastDailyReset 
          : new Date().toISOString(),
        dayStartTime: new Date()
      };
    }

    default:
      return state;
  }
}
